import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Box,
  Card,
  TextField,
  Grid,
  Typography,
  TablePagination,
} from "@mui/material";
import MoreMenu from "../Admin-PaymentMethod/MoreMenu.js";
import ListHead from "../table/ListHead";
import Scrollbar from "../table/Scrollbar";
import SearchNotFound from "../table/SearchNotFound";
import { applySortFilter } from "../../utils/tableFunctions";
import { getPaymentMethodFilterConditions, getPaymentMethods, PaymentMethodAction } from "../../services/payment";
import { Log } from "../../services/services.js";
import * as Yup from "yup";
import { useFormik } from "formik";
import { StyledTableRow } from "../TableRow.js";
import CircularLoader from "../circularLoader.js";
import FormDialog from "../FormDialog.js";
import Label from "../Label.js";
import FormSearch from "../FormSearch.js";

const defaultValue = {
  is_active: true
}

const TABLE_HEAD = [
  { id: "name", label: "Payment Method" },
  { id: "is_active", label: "Active" },
  { id: "menu" },
];

function PaymentMethod({ userRole }) {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [fLoaded, setFLoaded] = useState(false);
  const [methodFilter, setMethodFilter] = useState({});

  const [paymentMethods, setPaymentMethods] = useState({ results: [], count: 0 });
  const [currentFilter, setCurrentFilter] = useState(defaultValue);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 50,
  });

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    queryPage({
      page: newPage + 1,
      page_size: pagination.page_size,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    queryPage({
      page: 1,
      page_size: parseInt(event.target.value, 10),
    });
  };

  const queryPage = (queryParams) => {
    setLoaded(false);
    getPaymentMethods({
      setResponse: setPaymentMethods,
      setLoaded: setLoaded,
      filter: { ...currentFilter, ...queryParams },
    });
    setPagination({
      page: queryParams.page,
      page_size: queryParams.page_size,
    });
  };

  const filteredList = applySortFilter(
    paymentMethods.results || [],
    order,
    orderBy
  );

  const isItemNotFound = (paymentMethods.results || []).length === 0;

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedId, setSelectedId] = useState();
  const handleCloseDialog = () => {
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
    selectedId && setSelectedId();
  };

  useEffect(() => {
    getPaymentMethodFilterConditions(setMethodFilter, setFLoaded);
    getPaymentMethods({
      setResponse: setPaymentMethods,
      setLoaded: setLoaded,
      filter: { ...currentFilter, ...pagination }
    });
  }, []);

  const DeleteMethodDialog = ({ open, selectedId, setResponse, setLoaded }) => {
    const {
      handleChange,
      handleSubmit,
      handleBlur,
      values,
      touched,
      errors,
      isSubmitting,
      resetForm
    } = useFormik({
      initialValues: {
        reason: "",
      },
      validationSchema: Yup.object().shape({
        reason: Yup.string().required("Required").trim(),
      }),
      onSubmit: async (values) => {
        Log.info(`Click button [Submit] - Delete Payment Method by Id: ${selectedId}`);
        await PaymentMethodAction(
          "REMOVE",
          { selectedId, action_message: values.reason },
          setResponse,
          setLoaded
        );
        handleClose();
      }
    });

    const handleClose = () => {
      setOpenDeleteDialog(false);
      resetForm();
    };

    return (
      <FormDialog
        open={open}
        onClose={handleClose}
        title="Delete this payment method?"
        handleSubmit={handleSubmit}
        loading={isSubmitting}
        maxWidth="sm"
      >
        <Grid container sx={{ alignItems: "center" }} spacing={3}>
          <Grid item md={5} xs={5}>
            <Typography variant="subtitle1">Reason *</Typography>
          </Grid>
          <Grid item md={7} xs={7}>
            <TextField
              name="reason"
              value={values.reason}
              onChange={handleChange}
              onBlur={handleBlur}
              variant="outlined"
              fullWidth
              size="small"
              multiline
              rows={2}
              placeholder="Please enter your reason"
              error={Boolean(touched.reason && errors.reason)}
              helperText={touched.reason && errors.reason}
              disabled={isSubmitting}
            />
          </Grid>
        </Grid>
      </FormDialog>
    );
  };

  const EditMethodDialog = ({ open, selectedId, setResponse, setLoaded, onClose }) => {
    useEffect(() => {
      let editPaymentMethodHost = (paymentMethods.results || []).find(
        (e) => e.id === selectedId
      );
      setFieldValue("name", editPaymentMethodHost.name || "");
    }, []);

    const {
      handleChange,
      handleSubmit,
      handleBlur,
      errors,
      values,
      touched,
      isSubmitting,
      setFieldValue
    } = useFormik({
      initialValues: {
        name: "",
      },
      validationSchema: Yup.object().shape({
        name: Yup.string()
          .required("This field is required")
          .test('not-whitespace-only', 'This field is required', function (value) {
            return value && value.trim().length > 0;
          }),
      }),
      onSubmit: async (values, { setSubmitting }) => {
        const name = values.name.trim();
        Log.info(`Click button [Submit] - Update Payment Method details by Id: ${selectedId}`, { name });
        await PaymentMethodAction(
          "UPDATE",
          { selectedId, paymentMethod: name.trim() },
          setResponse,
          setLoaded
        );
        setOpenEditDialog(false);
        setSubmitting(false);
      },
    });

    return (
      <FormDialog
        open={open}
        onClose={onClose}
        title="Edit this payment method?"
        handleSubmit={handleSubmit}
        loading={isSubmitting}
        maxWidth="sm"
      >
        <TextField
          name="name"
          autoFocus
          margin="dense"
          label="Payment Method"
          fullWidth
          variant="standard"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          error={Boolean(touched.name && errors.name)}
          helperText={touched.name && errors.name}
          disabled={isSubmitting}
        />
      </FormDialog>
    );
  };

  const onFilterSubmit = (fParams) => {
    const newParams = {
      ...fParams,
      name: fParams.name?.label,
    }
    if (newParams.is_active === 'all') delete newParams.is_active;
    setCurrentFilter(newParams);
    setLoaded(false);
    queryPage({ ...newParams, page: 1, page_size: pagination.page_size });
  };

  const schema = [
    {
      type: "search-select",
      label: "Payment Method",
      param: "name",
      options: methodFilter?.name?.map((method) => ({
        label: method.name,
        id: method.id,
      })),
    },
    {
      type: "select",
      label: "Active",
      param: "is_active",
      options: [
        { name: "All", id: "all" },
        { name: "Yes", id: true },
        { name: "No", id: false },
      ]
    }
  ]

  return (
    <Box>
      <FormSearch
        schema={schema}
        loading={!fLoaded}
        onSearch={onFilterSubmit}
        defaultValue={defaultValue}
      />
      {loaded ?
        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  onRequestSort={handleRequestSort}
                  checkbox={false}
                />
                <TableBody>
                  {filteredList.map(({ id, name, is_active }) => {
                    return (
                      <StyledTableRow
                        hover
                        key={id}
                        sx={{ cursor: "default" }}
                      >
                        <TableCell children={name} id={id} />
                        <TableCell
                          children={
                            <Label color={is_active ? "success" : "error"}>
                              {is_active ? "YES" : "NO"}
                            </Label>
                          }
                          id={id}
                        />
                        {/* <TableCell align="center">
                            <Button
                              variant="text"
                              color="secondary"
                              onClick={() =>
                                window.open(
                                  `/3/app/payment-method-details/${id}`,
                                  "_blank"
                                )
                              }
                            >
                              View Details
                            </Button>
                          </TableCell> */}
                        <TableCell align="right">
                          {is_active && (
                            <MoreMenu
                              setOpenDeleteDialog={setOpenDeleteDialog}
                              setOpenEditDialog={setOpenEditDialog}
                              setRow={() => setSelectedId(id)}
                              userRole={userRole}
                            />
                          )}
                        </TableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
                {isItemNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <SearchNotFound />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={paymentMethods?.count || 0}
            rowsPerPage={pagination.page_size}
            page={pagination.page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
        :
        <CircularLoader />
      }
      {selectedId && (
        <>
          {openEditDialog && (
            <EditMethodDialog
              open={true}
              onClose={handleCloseDialog}
              selectedId={selectedId}
              setResponse={setPaymentMethods}
              setTableLoaded={setLoaded}
            />
          )}
          {openDeleteDialog && (
            <DeleteMethodDialog
              open={true}
              onClose={handleCloseDialog}
              selectedId={selectedId}
              setResponse={setPaymentMethods}
              setLoaded={setLoaded}
            />
          )}
        </>
      )}
    </Box>
  );
}

export default PaymentMethod;
